import type {FollowAccountType, FollowState} from "@buildwithflux/following-schema";
import type {StarState} from "@buildwithflux/starring-schema";

import {getCurrentIdToken} from "../firebase/firebaseClient";
import {getAccountsApiBase} from "../utils/apiBase";
import {fetchWithRetry} from "../utils/fetchWithRetry";

class NotAuthenticatedError extends Error {
    constructor() {
        super("Not authenticated");
        this.name = "NotAuthenticatedError";
    }
}

/**
 * POSTs an authenticated write, retrying transient failures (network errors and
 * 5xx responses) with exponential backoff. Client errors (4xx) are not retried
 * since they will not succeed on a repeat.
 */
async function postAuthed(path: string, body: unknown): Promise<void> {
    const token = await getCurrentIdToken();
    if (!token) {
        throw new NotAuthenticatedError();
    }

    const res = await fetchWithRetry(() =>
        fetch(`${getAccountsApiBase()}${path}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
        }),
    );

    if (!res.ok) {
        throw new Error(`${path} failed: ${res.status}`);
    }
}

/** Follow/unfollow an account via the accounts-app HTTP write endpoint. */
export function writeFollow(state: FollowState, accountUid: string, accountType: FollowAccountType): Promise<void> {
    return postAuthed("/accounts/following", {state, accountUid, accountType});
}

/** Star/unstar a document via the accounts-app HTTP write endpoint. */
export function writeStar(state: StarState, documentUid: string): Promise<void> {
    return postAuthed("/accounts/starring", {state, documentUid});
}
